import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { LoginButton } from "../components/auth/LoginButton"
import { Alert, AlertDescription } from "../components/ui/alert"
import { getRedirectInfo, clearRedirectReason } from "../utils/auth"
import { useAtomValue } from "jotai";
import { authAtom } from "../store/auth";
import { Navigate } from "react-router-dom";
import { Shield, Users, Zap} from "lucide-react";
import { Badge } from "../components/ui/badge";
import { ThemeToggle } from "../components/ui/theme-toggle";

// Modus Logo Component
const ModusLogo = ({ className = "", ...props }) => (
  <svg 
    className={className}
    height="37" 
    xmlns="http://www.w3.org/2000/svg" 
    width="37" 
    viewBox="0 0 37 37"
    {...props}
  >
    <g fill="none" fillRule="evenodd">
      <path 
        className="logo-circle" 
        d="M36.5,18.3A18.1,18.1,0,0,1,18.3,36.5,18.3,18.3,0,1,1,36.5,18.3Z" 
        fill="#50B848"
      />
      <path 
        className="logo-lines" 
        d="M26.6,12.8v11A2.2,2.2,0,0,1,24.4,26a2.2,2.2,0,0,1-2.3-2.2v-11a2.3,2.3,0,1,1,4.5,0Z M20.5,12.8v7.6a2.2,2.2,0,0,1-2.2,2.2,2.3,2.3,0,0,1-2.4-2.2V12.8a2.4,2.4,0,0,1,2.4-2.4A2.4,2.4,0,0,1,20.5,12.8Z M14.4,12.8v11A2.2,2.2,0,0,1,12.1,26a2.3,2.3,0,0,1-2.3-2.2v-11a2.3,2.3,0,0,1,2.3-2.4A2.4,2.4,0,0,1,14.4,12.8Z" 
        fill="#FFF"
      />
    </g>
  </svg>
);

export function LoginPage() {
  const [redirectMessage, setRedirectMessage] = useState<string | null>(null);
  const [redirectType, setRedirectType] = useState<'error' | 'info'>('info');
  const { isAuthenticated } = useAtomValue(authAtom);

  useEffect(() => {
    // Check if we have redirect information
    const { reason } = getRedirectInfo();
    if (reason) {
      setRedirectMessage(reason);
      // Determine if this is an error or info message
      setRedirectType(reason.toLowerCase().includes('error') || 
                     reason.toLowerCase().includes('failed') || 
                     reason.toLowerCase().includes('denied') ? 'error' : 'info');
      // Do not clear auth_redirect_path — AuthCallback needs it after OAuth
      clearRedirectReason();
    }
  }, []);

  // Redirect to dashboard if already authenticated
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Theme toggle positioned in top-right corner */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      <div className="container flex min-h-screen w-screen flex-col items-center justify-center px-4 py-8">
        {/* Redirect message alert */}
        {redirectMessage && (
          <Alert 
            variant={redirectType === 'error' ? 'destructive' : 'default'}
            className="mb-6 max-w-md w-full"
          >
            <AlertDescription className="text-sm">
              {redirectMessage}
            </AlertDescription>
          </Alert>
        )}

        {/* Main login card */}
        <Card className="w-full max-w-md shadow-lg border-0 bg-white/80 backdrop-blur-sm dark:bg-gray-800/80">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center">
              <ModusLogo className="h-12 w-12" />
            </div>
            <CardTitle className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
              Welcome to Modus App Directory
            </CardTitle>
            <CardDescription className="text-base text-gray-600 dark:text-gray-300 mt-2">
              Your central hub for all organizational tools and applications
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Login section */}
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Sign in with your Google Workspace account
                </p>
                <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700">
                  @modusdigital.com accounts only
                </Badge>
              </div>
              
              <LoginButton />
              
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                By signing in, you agree to your organization's IT policies
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Feature preview */}
        <div className="mt-8 max-w-md w-full">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">What you'll get access to:</h3>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex h-8 w-8 items-center justify-center rounded bg-blue-100 dark:bg-blue-900/50">
                <Zap className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-white">Discover & Organize</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Find all available tools in one searchable directory</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex h-8 w-8 items-center justify-center rounded bg-green-100 dark:bg-green-900/50">
                <Users className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-white">Self-Service Requests</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Request new tools and track approvals in real-time</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <div className="flex h-8 w-8 items-center justify-center rounded bg-purple-100 dark:bg-purple-900/50">
                <Shield className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-white">Everything in One Place</p>
                <p className="text-xs text-gray-600 dark:text-gray-400">Your personal dashboard for all work applications</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-gray-500 dark:text-gray-400">
          <p>Need help? Contact your IT administrator</p>
        </div>
      </div>
    </div>
  )
} 